<template>
    <div class="analytics-dashboard container-fluid py-4">
        <!-- Header -->
        <div class="row mb-4">
            <div class="col-12">
                <h1 class="text-gradient mb-2">Solana Analytics</h1>
                <p class="analytics-text-muted">Real-time network metrics powered by deep transaction indexing</p>
            </div>
        </div>

        <!-- Hero Stats - Last 24 Hours -->
        <div class="analytics-hero mb-4">
            <div class="analytics-hero-stat">
                <div class="hero-label">24h Transactions</div>
                <div class="hero-value">{{ formatCompact(summary.totalTransactions) }}</div>
                <div class="hero-secondary">{{ formatPercent(summary.completionRate) }} completed successfully</div>
            </div>
            <div class="analytics-hero-stat hero-stat--green">
                <div class="hero-label">24h Validator Revenue</div>
                <div class="hero-value">{{ formatSOL(summary.totalRevenue) }}</div>
                <div class="hero-secondary">from fees + Jito tips</div>
            </div>
            <div class="analytics-hero-stat hero-stat--amber">
                <div class="hero-label">24h DEX Volume</div>
                <div class="hero-value">${{ formatCompact(defi.totalVolume) }}</div>
                <div class="hero-secondary">{{ formatCompact(defi.totalTraders) }} unique traders</div>
            </div>
            <div class="analytics-hero-stat hero-stat--blue">
                <div class="hero-label">24h Active Wallets</div>
                <div class="hero-value">{{ formatCompact(accounts.totalUniqueWallets) }}</div>
                <div class="hero-secondary">avg {{ accounts.avgTxPerWallet?.toFixed(1) || '-' }} tx each</div>
            </div>
        </div>

        <!-- Dashboard Navigation -->
        <div class="section-divider">
            <h2 class="section-title">Explore Dashboards</h2>
            <p class="section-subtitle">Click any card to dive deeper into the data</p>
        </div>

        <div class="row g-4 mb-4">
            <!-- Network Performance -->
            <div class="col-lg-4 col-md-6">
                <a href="/analytics/network" class="dashboard-card dashboard-card--blue">
                    <div class="dashboard-card-header">
                        <h3>Network Performance</h3>
                        <span class="dashboard-card-cta">Explore →</span>
                    </div>
                    <p class="dashboard-card-desc">Real-time TPS charts, transaction completion rates, and network health metrics</p>
                    <div class="dashboard-card-stats">
                        <div class="dashboard-stat">
                            <span class="dashboard-stat-value">{{ summary.avgTps || '-' }}</span>
                            <span class="dashboard-stat-label">Avg TPS</span>
                        </div>
                        <div class="dashboard-stat">
                            <span class="dashboard-stat-value text-success">{{ formatPercent(summary.completionRate) }}</span>
                            <span class="dashboard-stat-label">Success Rate</span>
                        </div>
                        <div class="dashboard-stat">
                            <span class="dashboard-stat-value text-danger">{{ formatPercent(summary.revertRate) }}</span>
                            <span class="dashboard-stat-label">Revert Rate</span>
                        </div>
                    </div>
                </a>
            </div>

            <!-- Fee Economics -->
            <div class="col-lg-4 col-md-6">
                <a href="/analytics/fees" class="dashboard-card dashboard-card--purple">
                    <div class="dashboard-card-header">
                        <h3>Fee Economics</h3>
                        <span class="dashboard-card-cta">Explore →</span>
                    </div>
                    <p class="dashboard-card-desc">Fee breakdown by type, percentile distributions, and optimal fee calculator</p>
                    <div class="dashboard-card-stats">
                        <div class="dashboard-stat">
                            <span class="dashboard-stat-value">{{ formatSOL(summary.totalFees) }}</span>
                            <span class="dashboard-stat-label">Total Fees</span>
                        </div>
                        <div class="dashboard-stat">
                            <span class="dashboard-stat-value">{{ formatLamports(percentiles.fee?.[50]) }}</span>
                            <span class="dashboard-stat-label">Median Fee</span>
                        </div>
                        <div class="dashboard-stat">
                            <span class="dashboard-stat-value" style="color: #f97316;">{{ formatSOL(summary.totalJitoTips) }}</span>
                            <span class="dashboard-stat-label">Jito Tips</span>
                        </div>
                    </div>
                </a>
            </div>

            <!-- DeFi & Trading -->
            <div class="col-lg-4 col-md-6">
                <a href="/analytics/defi" class="dashboard-card dashboard-card--pink">
                    <div class="dashboard-card-header">
                        <h3>DeFi & Trading</h3>
                        <span class="dashboard-card-cta">Explore →</span>
                    </div>
                    <p class="dashboard-card-desc">DEX volume leaderboards, aggregator routing, and token trading activity</p>
                    <div class="dashboard-card-stats">
                        <div class="dashboard-stat">
                            <span class="dashboard-stat-value">${{ formatCompact(defi.totalVolume) }}</span>
                            <span class="dashboard-stat-label">Volume</span>
                        </div>
                        <div class="dashboard-stat">
                            <span class="dashboard-stat-value">{{ formatCompact(defi.totalTrades) }}</span>
                            <span class="dashboard-stat-label">Trades</span>
                        </div>
                        <div class="dashboard-stat">
                            <span class="dashboard-stat-value">{{ defi.totalPrograms }}</span>
                            <span class="dashboard-stat-label">DEX Programs</span>
                        </div>
                    </div>
                </a>
            </div>

            <!-- Program Analytics -->
            <div class="col-lg-4 col-md-6">
                <a href="/analytics/programs" class="dashboard-card dashboard-card--cyan">
                    <div class="dashboard-card-header">
                        <h3>Program Analytics</h3>
                        <span class="dashboard-card-cta">Explore →</span>
                    </div>
                    <p class="dashboard-card-desc">Top programs by transactions, fees earned, and compute usage breakdown</p>
                    <div class="dashboard-card-stats">
                        <div class="dashboard-stat">
                            <span class="dashboard-stat-value">{{ programs.totalPrograms }}</span>
                            <span class="dashboard-stat-label">Active Programs</span>
                        </div>
                        <div class="dashboard-stat" v-if="programs.topProgram">
                            <span class="dashboard-stat-value">{{ formatPubkey(programs.topProgram) }}</span>
                            <span class="dashboard-stat-label">Top by Volume</span>
                        </div>
                    </div>
                </a>
            </div>

            <!-- Account Activity -->
            <div class="col-lg-4 col-md-6">
                <a href="/analytics/accounts" class="dashboard-card dashboard-card--teal">
                    <div class="dashboard-card-header">
                        <h3>Account Activity</h3>
                        <span class="dashboard-card-cta">Explore →</span>
                    </div>
                    <p class="dashboard-card-desc">Hot writable accounts, wallet activity patterns, and read/write metrics</p>
                    <div class="dashboard-card-stats">
                        <div class="dashboard-stat">
                            <span class="dashboard-stat-value">{{ formatCompact(accounts.totalUniqueWallets) }}</span>
                            <span class="dashboard-stat-label">Active Wallets</span>
                        </div>
                        <div class="dashboard-stat">
                            <span class="dashboard-stat-value">{{ formatCompact(accounts.totalWrites) }}</span>
                            <span class="dashboard-stat-label">Account Writes</span>
                        </div>
                    </div>
                </a>
            </div>

        </div>

        <!-- 24h Trend Charts -->
        <div class="section-divider">
            <h2 class="section-title">24 Hour Trends</h2>
        </div>

        <div class="analytics-row cols-2 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Fee Revenue (SOL)</div>
                    <div class="panel-subtitle">Hourly breakdown</div>
                </div>
                <div class="panel-body">
                    <canvas ref="feeTrendChart" height="200"></canvas>
                </div>
            </div>

            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Transaction Volume</div>
                    <div class="panel-subtitle">Hourly breakdown</div>
                </div>
                <div class="panel-body">
                    <canvas ref="txTrendChart" height="200"></canvas>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Chart from 'chart.js/auto';
import { colors, getLineChartOptions, getBarChartOptions, getThemeAwareColors } from '../composables/useChartConfig';

export default {
    name: 'AnalyticsOverview',

    data() {
        return {
            summary: {
                totalTransactions: 0,
                totalFees: 0,
                totalRevenue: 0,
                totalJitoTips: 0,
                revertedTransactions: 0,
                completionRate: 0,
                revertRate: 0,
                avgTps: 0
            },
            defi: {
                totalVolume: 0,
                totalTrades: 0,
                totalTraders: 0,
                totalPrograms: 0
            },
            programs: {
                totalPrograms: 0,
                topProgram: null
            },
            accounts: {
                totalUniqueWallets: 0,
                avgTxPerWallet: 0,
                totalWrites: 0
            },
            percentiles: {
                fee: {}
            },
            timeSeries: [],
            refreshInterval: null
        };
    },

    created() {
        this.charts = {};
    },

    mounted() {
        this.initCharts();
        this.loadData();

        this.refreshInterval = setInterval(() => {
            this.loadData();
        }, 60000);
    },

    beforeUnmount() {
        Object.values(this.charts).forEach(c => c?.destroy());
        if (this.refreshInterval) clearInterval(this.refreshInterval);
    },

    methods: {
        initCharts() {
            const theme = getThemeAwareColors();

            this.charts.feeTrend = new Chart(this.$refs.feeTrendChart, {
                type: 'line',
                data: { labels: [], datasets: [] },
                options: {
                    ...getLineChartOptions({ useDynamicColors: true }),
                    plugins: { legend: { display: false } },
                    scales: {
                        x: { display: true, ticks: { color: theme.textMuted, maxTicksLimit: 8 } },
                        y: {
                            min: 0,
                            ticks: {
                                color: theme.textMuted,
                                callback: v => v.toFixed(0)
                            },
                            grid: { color: theme.grid }
                        }
                    }
                }
            });

            this.charts.txTrend = new Chart(this.$refs.txTrendChart, {
                type: 'bar',
                data: { labels: [], datasets: [] },
                options: {
                    ...getBarChartOptions({ useDynamicColors: true }),
                    plugins: { legend: { display: false } },
                    scales: {
                        x: { display: true, ticks: { color: theme.textMuted, maxTicksLimit: 8 } },
                        y: {
                            min: 0,
                            ticks: {
                                color: theme.textMuted,
                                callback: v => this.formatCompact(v)
                            },
                            grid: { color: theme.grid }
                        }
                    }
                }
            });
        },

        async loadData() {
            try {
                // Load fee metrics
                const feeRes = await fetch('/analytics/api/fee-metrics?range=24h&interval=1h');
                const feeData = await feeRes.json();

                const totalTx = feeData.summary?.totalTransactions || 0;
                const revertedTx = feeData.summary?.revertedTransactions || 0;
                const completionRate = feeData.summary?.completionRate || 0;

                this.summary = {
                    totalTransactions: totalTx,
                    totalFees: feeData.summary?.totalFees || 0,
                    totalRevenue: feeData.summary?.totalRevenue || 0,
                    totalJitoTips: feeData.summary?.totalJitoTips || 0,
                    revertedTransactions: revertedTx,
                    completionRate: completionRate,
                    revertRate: totalTx > 0 ? ((revertedTx / totalTx) * 100) : 0,
                    avgTps: Math.round(totalTx / (24 * 60 * 60)) // rough TPS over 24h
                };

                this.percentiles = feeData.aggregatedPercentiles || { fee: {} };
                this.timeSeries = feeData.timeSeries || [];

                // Update charts
                if (this.timeSeries.length > 0) {
                    const labels = this.timeSeries.map(d => d.label);
                    const feeValues = this.timeSeries.map(d => d.totalFees);
                    const txValues = this.timeSeries.map(d => d.txCount);

                    this.charts.feeTrend.data = {
                        labels,
                        datasets: [{
                            data: feeValues,
                            borderColor: colors.primary,
                            backgroundColor: 'rgba(139, 92, 246, 0.1)',
                            fill: true,
                            tension: 0.3,
                            borderWidth: 2,
                            pointRadius: 0
                        }]
                    };
                    this.charts.feeTrend.update();

                    this.charts.txTrend.data = {
                        labels,
                        datasets: [{
                            data: txValues,
                            backgroundColor: colors.accent + '99',
                            borderWidth: 0,
                            borderRadius: 2
                        }]
                    };
                    this.charts.txTrend.update();
                }

                // Load DeFi metrics
                try {
                    const defiRes = await fetch('/analytics/api/defi-metrics?range=24h');
                    const defiData = await defiRes.json();
                    this.defi = {
                        totalVolume: defiData.summary?.totalVolume || 0,
                        totalTrades: defiData.summary?.totalTrades || 0,
                        totalTraders: defiData.summary?.totalTraders || 0,
                        totalPrograms: defiData.summary?.totalPrograms || 0
                    };
                } catch (e) {
                    console.warn('DeFi metrics not available:', e);
                }

                // Load program metrics
                try {
                    const progRes = await fetch('/analytics/api/program-metrics?range=24h&limit=1');
                    const progData = await progRes.json();
                    this.programs.totalPrograms = progData.totalPrograms || 0;
                    if (progData.programs?.[0]?.program) {
                        this.programs.topProgram = progData.programs[0].program;
                    }
                } catch (e) {
                    console.warn('Program metrics not available:', e);
                }

                // Load account metrics
                try {
                    const accRes = await fetch('/analytics/api/account-metrics?range=24h');
                    const accData = await accRes.json();
                    this.accounts = {
                        totalUniqueWallets: accData.summary?.totalUniqueWallets || 0,
                        avgTxPerWallet: accData.summary?.avgTxPerWallet || 0,
                        totalWrites: accData.summary?.totalWrites || 0
                    };
                } catch (e) {
                    console.warn('Account metrics not available:', e);
                }

            } catch (error) {
                console.error('Error loading overview data:', error);
            }
        },

        formatCompact(value) {
            if (!value) return '0';
            if (value >= 1e9) return (value / 1e9).toFixed(2) + 'B';
            if (value >= 1e6) return (value / 1e6).toFixed(2) + 'M';
            if (value >= 1e3) return (value / 1e3).toFixed(1) + 'K';
            return value.toLocaleString();
        },

        formatSOL(value) {
            if (!value) return '0 SOL';
            if (value >= 1000) return (value / 1000).toFixed(1) + 'K SOL';
            return value.toFixed(2) + ' SOL';
        },

        formatLamports(value) {
            if (!value) return '0';
            if (value >= 1e9) return (value / 1e9).toFixed(4) + ' SOL';
            if (value >= 1e6) return (value / 1e6).toFixed(1) + 'M';
            if (value >= 1e3) return (value / 1e3).toFixed(1) + 'K';
            return Math.round(value).toLocaleString();
        },

        formatPercent(value) {
            if (value === null || value === undefined) return '-';
            return value.toFixed(1) + '%';
        },

        formatPubkey(address) {
            if (!address) return '-';
            return address.slice(0, 4) + '...' + address.slice(-4);
        }
    }
};
</script>

<style scoped>
/* Dashboard Cards - Clickable, colorful CTAs */
.dashboard-card {
    display: block;
    padding: 1.5rem;
    border-radius: var(--compass-radius, 12px);
    text-decoration: none;
    color: inherit;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    height: 100%;
    position: relative;
    overflow: hidden;
}

.dashboard-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
}

/* Color variants */
.dashboard-card--blue {
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.12) 0%, rgba(59, 130, 246, 0.04) 100%);
    border: 1px solid rgba(59, 130, 246, 0.25);
}
.dashboard-card--blue::before { background: linear-gradient(90deg, #3b82f6, #60a5fa); }
.dashboard-card--blue:hover { border-color: rgba(59, 130, 246, 0.5); box-shadow: 0 8px 32px rgba(59, 130, 246, 0.2); }

.dashboard-card--purple {
    background: linear-gradient(135deg, rgba(139, 92, 246, 0.12) 0%, rgba(139, 92, 246, 0.04) 100%);
    border: 1px solid rgba(139, 92, 246, 0.25);
}
.dashboard-card--purple::before { background: linear-gradient(90deg, #8b5cf6, #a78bfa); }
.dashboard-card--purple:hover { border-color: rgba(139, 92, 246, 0.5); box-shadow: 0 8px 32px rgba(139, 92, 246, 0.2); }

.dashboard-card--pink {
    background: linear-gradient(135deg, rgba(236, 72, 153, 0.12) 0%, rgba(236, 72, 153, 0.04) 100%);
    border: 1px solid rgba(236, 72, 153, 0.25);
}
.dashboard-card--pink::before { background: linear-gradient(90deg, #ec4899, #f472b6); }
.dashboard-card--pink:hover { border-color: rgba(236, 72, 153, 0.5); box-shadow: 0 8px 32px rgba(236, 72, 153, 0.2); }

.dashboard-card--cyan {
    background: linear-gradient(135deg, rgba(16, 247, 207, 0.1) 0%, rgba(16, 247, 207, 0.03) 100%);
    border: 1px solid rgba(16, 247, 207, 0.25);
}
.dashboard-card--cyan::before { background: linear-gradient(90deg, #10f7cf, #5eead4); }
.dashboard-card--cyan:hover { border-color: rgba(16, 247, 207, 0.5); box-shadow: 0 8px 32px rgba(16, 247, 207, 0.15); }

.dashboard-card--teal {
    background: linear-gradient(135deg, rgba(20, 184, 166, 0.12) 0%, rgba(20, 184, 166, 0.04) 100%);
    border: 1px solid rgba(20, 184, 166, 0.25);
}
.dashboard-card--teal::before { background: linear-gradient(90deg, #14b8a6, #2dd4bf); }
.dashboard-card--teal:hover { border-color: rgba(20, 184, 166, 0.5); box-shadow: 0 8px 32px rgba(20, 184, 166, 0.2); }

.dashboard-card:hover {
    transform: translateY(-4px);
}

.dashboard-card-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 0.5rem;
}

.dashboard-card-header h3 {
    font-size: 1.1rem;
    font-weight: 700;
    margin: 0;
}

.dashboard-card-cta {
    font-size: 0.85rem;
    font-weight: 600;
    opacity: 0.7;
    transition: opacity 0.2s, transform 0.2s;
}

.dashboard-card:hover .dashboard-card-cta {
    opacity: 1;
    transform: translateX(4px);
}

.dashboard-card-desc {
    font-size: 0.85rem;
    opacity: 0.7;
    margin-bottom: 1rem;
    line-height: 1.4;
}

.dashboard-card-stats {
    display: flex;
    gap: 1.25rem;
    flex-wrap: wrap;
}

.dashboard-stat {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
}

.dashboard-stat-value {
    font-size: 1.1rem;
    font-weight: 700;
    font-family: 'JetBrains Mono', monospace;
}

.dashboard-stat-label {
    font-size: 0.65rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    opacity: 0.6;
}
</style>
